import { fetchEvents } from './api'

// Durable events carry a contiguous seq. A hole means a missed event: close it from the
// replay endpoint before live events continue.
export default function createConversationSubscription (consumer, id, onEvent) {
  let subscription = null
  let lastSeq = 0
  let catchingUp = false
  let queued = []
  let failures = 0
  let retryTimer

  function connect () {
    subscription = consumer.subscriptions.create(
      { channel: 'VeraChannel', conversation_id: id },
      {
        connected: () => catchUp(),
        received: (event) => receive(event)
      }
    )
  }

  function disconnect () {
    subscription?.unsubscribe()
    subscription = null
    clearTimeout(retryTimer)
    catchingUp = false
    queued = []
  }

  function receive (event) {
    if (catchingUp) {
      queued.push(event)
      return
    }

    if (event.seq === null || event.seq === undefined) return onEvent(event)

    if (event.seq > lastSeq + 1) return catchUp(event)

    if (event.seq <= lastSeq) return

    lastSeq = event.seq
    onEvent(event)
  }

  function catchUp (heldEvent) {
    if (heldEvent) queued.push(heldEvent)
    if (catchingUp) return

    catchingUp = true
    return replay()
  }

  async function replay () {
    const events = await fetchEvents(id, lastSeq).then(response => response.events, () => null)
    if (!subscription) return
    if (!events) return retryLater()

    // The live event that revealed the hole can be ahead of the replay: retry until filled.
    if (events.length === 0 && stillMissing()) return retryLater()

    failures = 0
    for (const event of events) {
      if (event.seq !== lastSeq + 1) return retryLater()
      lastSeq = event.seq
      onEvent(event)
    }
    catchingUp = false
    const held = queued
    queued = []
    held.forEach(receive)
  }

  function stillMissing () {
    const held = queued.map(event => event.seq).filter(seq => seq !== null && seq !== undefined)
    return held.length > 0 && Math.min(...held) > lastSeq + 1
  }

  // A failed replay is retried with backoff (1s doubling to 30s): live events wait in the queue meanwhile.
  function retryLater () {
    if (!subscription) return

    const delay = Math.min(1000 * 2 ** failures, 30000)
    failures += 1
    retryTimer = setTimeout(replay, delay)
  }

  return { id, connect, disconnect, receive }
}
